/**
 * <axiom-tabs> — accessible tabs with an animated indicator and cross-fading panels.
 * Markup: [role=tablist] > [role=tab][aria-controls], [role=tabpanel][id]
 */
(function () {
  'use strict';

  if (customElements.get('axiom-tabs')) return;

  class AxiomTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.indicator = this.querySelector('.tabs__indicator');
      if (!this.tabs.length) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true), { signal });
        tab.addEventListener('keydown', (event) => this.onKey(event, index), { signal });
      });
      window.addEventListener('resize', () => this.moveIndicator(), { passive: true, signal });

      const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(selected < 0 ? 0 : selected, false);

      if (this.dataset.autoplay) this.startAutoplay(parseFloat(this.dataset.autoplay) * 1000, signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearInterval(this.timer);
    }

    startAutoplay(interval, signal) {
      if (!interval || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const restart = () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => this.select((this.current + 1) % this.tabs.length, false), interval);
      };
      this.addEventListener('pointerenter', () => clearInterval(this.timer), { signal });
      this.addEventListener('pointerleave', restart, { signal });
      this.addEventListener('focusin', () => clearInterval(this.timer), { signal });
      restart();
    }

    onKey(event, index) {
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.select(next, true);
    }

    select(index, focus) {
      this.current = index;
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls')));
        if (!panel) return;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (focus) this.tabs[index].focus();
      this.moveIndicator();
      window.AxiomMotion?.refresh();
    }

    moveIndicator() {
      if (!this.indicator || !this.tabs[this.current]) return;
      const tab = this.tabs[this.current];
      this.indicator.style.width = tab.offsetWidth + 'px';
      this.indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
    }
  }

  customElements.define('axiom-tabs', AxiomTabs);
})();
